import React from 'react';
import { Box, CircularProgress, FormControl, FormLabel, IconButton, Typography } from '@mui/material';
import { CloudUploadOutlined, Close as CloseIcon } from '@mui/icons-material';
import { MAX_FILE_SIZE_IN_MEGA_BYTES } from '@/utils/constants';

interface CompanyLogoDropzoneProps {
  companyLogoUrl: string | null;
  isLogoUploading: boolean;
  pendingLogoSignedId: string | null;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  onFile: (file: File | undefined) => void;
  onClear: () => void;
}

const CompanyLogoDropzone: React.FC<CompanyLogoDropzoneProps> = ({
  companyLogoUrl,
  isLogoUploading,
  pendingLogoSignedId,
  fileInputRef,
  onFile,
  onClear,
}) => {
  return (
    <FormControl fullWidth>
      <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>Company logo (optional)</FormLabel>
      <Box
        role="button"
        aria-label="Upload company logo"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!isLogoUploading) fileInputRef.current?.click();
          }
        }}
        onClick={() => {
          if (!isLogoUploading) fileInputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void onFile(e.dataTransfer.files[0]);
        }}
        sx={{
          position: 'relative',
          minHeight: 140,
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider',
          bgcolor: 'action.hover',
          cursor: isLogoUploading ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          '&:hover': {
            borderColor: isLogoUploading ? 'divider' : 'primary.main',
            bgcolor: isLogoUploading ? 'action.hover' : 'action.selected',
          },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            void onFile(e.target.files?.[0]);
            e.target.value = '';
          }}
          disabled={isLogoUploading}
        />
        {companyLogoUrl ? (
          <>
            <Box
              component="img"
              alt="Company logo preview"
              src={companyLogoUrl}
              sx={{
                maxWidth: '100%',
                maxHeight: 120,
                objectFit: 'contain',
                p: 1,
                opacity: isLogoUploading ? 0.45 : 1,
                transition: 'opacity 0.2s ease',
              }}
            />
            {isLogoUploading && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'action.disabledBackground',
                    borderRadius: 1,
                  }}
                  aria-hidden
                />
                <CircularProgress size={40} sx={{ position: 'absolute' }} aria-label="Uploading logo" />
              </>
            )}
            {pendingLogoSignedId && !isLogoUploading && (
              <IconButton
                type="button"
                size="small"
                aria-label="Remove selected logo"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'background.paper' },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </>
        ) : isLogoUploading ? (
          <CircularProgress size={40} aria-label="Uploading logo" />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 2,
              px: 2,
              textAlign: 'center',
            }}
          >
            <CloudUploadOutlined color="action" sx={{ fontSize: 40, opacity: 0.85 }} />
            <Typography variant="body2" color="text.secondary">
              Drag and drop an image here, or click to browse
            </Typography>
            <Typography variant="caption" color="text.disabled">
              PNG, JPG, WebP up to {MAX_FILE_SIZE_IN_MEGA_BYTES} MB
            </Typography>
          </Box>
        )}
      </Box>
    </FormControl>
  );
};

export default CompanyLogoDropzone;
